<template>
  <AppLayout 
    page-title="Timesheets" 
    page-subtitle="Track your work hours and manage time entries"
  >
    <!-- Page Header -->
    <div class="mb-8 pt-8">
      <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
        <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
        Time Tracking
      </div>
      <h1 class="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">Timesheets</h1>
      <p class="text-xl text-slate-300 mb-8 max-w-2xl">Track your work hours, manage time entries, and submit timesheets for approval</p>
    </div>

    <!-- Quick Stats -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
      <div class="timesheet-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">This Week</p>
          <p class="text-3xl font-bold text-white">{{ timeStats.thisWeek }}h</p>
          <p class="text-sm text-emerald-400">{{ timeStats.thisWeekTarget }}h target</p>
        </div>
      </div>

      <div class="timesheet-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-cyan-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">Pending Approval</p>
          <p class="text-3xl font-bold text-white">{{ timeStats.pendingApproval }}</p>
          <p class="text-sm text-amber-400">timesheets</p>
        </div>
      </div>

      <div class="timesheet-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-slate-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-slate-300" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">This Month</p>
          <p class="text-3xl font-bold text-white">{{ timeStats.thisMonth }}h</p>
          <p class="text-sm text-blue-400">+8% from last month</p>
        </div>
      </div>

      <div class="timesheet-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">Active Jobs</p>
          <p class="text-3xl font-bold text-white">{{ timeStats.activeJobs }}</p>
          <p class="text-sm text-purple-400">this week</p>
        </div>
      </div>
    </div>

    <!-- Current Week Timesheet -->
    <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-8">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-2xl font-bold text-white">Current Week Timesheet</h2>
        <div class="flex space-x-3">
          <button 
            @click="showCreateEntryModal = true"
            class="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors"
          >
            Create Timesheet Entry
          </button>
          <button 
            @click="showScheduledShiftsModal = true"
            class="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors"
          >
            View Scheduled Shifts
          </button>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="border-b border-slate-700/50">
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Date</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Job</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Start Time</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">End Time</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Hours</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Status</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="entry in currentWeekEntries" :key="entry.id" class="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors">
              <td class="py-4 px-4">
                <p class="text-white">{{ formatDate(entry.date) }}</p>
                <p class="text-sm text-slate-400">{{ formatDay(entry.date) }}</p>
              </td>
              <td class="py-4 px-4">
                <div>
                  <p class="text-white font-medium">{{ entry.jobTitle }}</p>
                  <p class="text-sm text-slate-400">{{ entry.clientName }}</p>
                </div>
              </td>
              <td class="py-4 px-4">
                <p class="text-white">{{ entry.startTime }}</p>
              </td>
              <td class="py-4 px-4">
                <p class="text-white">{{ entry.endTime || '--' }}</p>
              </td>
              <td class="py-4 px-4">
                <p class="text-white font-bold">{{ entry.hours }}h</p>
              </td>
              <td class="py-4 px-4">
                <span :class="getStatusClass(entry.status)" class="px-3 py-1 rounded-full text-xs font-medium">
                  {{ entry.status }}
                </span>
              </td>
              <td class="py-4 px-4">
                <div class="flex space-x-2">
                  <button class="text-emerald-400 hover:text-emerald-300 transition-colors">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                    </svg>
                  </button>
                  <button class="text-red-400 hover:text-red-300 transition-colors" @click="deleteEntry(entry)">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Recent Timesheets -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <h3 class="text-xl font-bold text-white mb-4">Recent Timesheets</h3>
        <div class="space-y-4">
          <div v-for="timesheet in recentTimesheets" :key="timesheet.id" class="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
            <div>
              <p class="text-white font-medium">{{ timesheet.period }}</p>
              <p class="text-sm text-slate-400">{{ timesheet.totalHours }} hours</p>
            </div>
            <div class="text-right">
              <p class="text-white font-bold">{{ timesheet.status }}</p>
              <p class="text-sm text-slate-400">{{ formatDate(timesheet.submittedDate) }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <h3 class="text-xl font-bold text-white mb-4">Quick Actions</h3>
        <div class="space-y-4">
          <button @click="showCreateEntryModal = true" class="w-full flex items-center justify-between p-4 bg-slate-700/30 rounded-lg hover:bg-slate-600/50 transition-colors">
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
                </svg>
              </div>
              <div>
                <p class="text-white font-medium">Create Timesheet Entry</p>
                <p class="text-sm text-slate-400">Add new time entry</p>
              </div>
            </div>
            <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>

          <button @click="showScheduledShiftsModal = true" class="w-full flex items-center justify-between p-4 bg-slate-700/30 rounded-lg hover:bg-slate-600/50 transition-colors">
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd"/>
                </svg>
              </div>
              <div>
                <p class="text-white font-medium">View Scheduled Shifts</p>
                <p class="text-sm text-slate-400">Create from schedule</p>
              </div>
            </div>
            <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>

          <button @click="submitTimesheet" class="w-full flex items-center justify-between p-4 bg-slate-700/30 rounded-lg hover:bg-slate-600/50 transition-colors">
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clip-rule="evenodd"/>
                </svg>
              </div>
              <div>
                <p class="text-white font-medium">Submit Timesheet</p>
                <p class="text-sm text-slate-400">Submit for approval</p>
              </div>
            </div>
            <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>

          <button class="w-full flex items-center justify-between p-4 bg-slate-700/30 rounded-lg hover:bg-slate-600/50 transition-colors">
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clip-rule="evenodd"/>
                </svg>
              </div>
              <div>
                <p class="text-white font-medium">Export Report</p>
                <p class="text-sm text-slate-400">Download timesheet</p>
              </div>
            </div>
            <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Create Timesheet Entry Modal -->
    <div v-if="showCreateEntryModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 w-full max-w-md">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-bold text-white">Create Timesheet Entry</h3>
          <button @click="showCreateEntryModal = false" class="text-slate-400 hover:text-white">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <form @submit.prevent="createTimesheetEntry" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Date</label>
            <input v-model="timesheetEntryForm.date" type="date" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Job Title</label>
            <input v-model="timesheetEntryForm.jobTitle" type="text" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white" placeholder="e.g., Pipeline Maintenance">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Client Name</label>
            <input v-model="timesheetEntryForm.clientName" type="text" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white" placeholder="e.g., ExxonMobil">
          </div>
          
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Start Time</label>
              <input v-model="timesheetEntryForm.startTime" type="time" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">End Time</label>
              <input v-model="timesheetEntryForm.endTime" type="time" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
            </div>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Description</label>
            <textarea v-model="timesheetEntryForm.description" rows="3" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white" placeholder="Brief description of work performed..."></textarea>
          </div>
          
          <div class="flex space-x-3">
            <button type="button" @click="showCreateEntryModal = false" class="flex-1 px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg">
              Cancel
            </button>
            <button type="submit" class="flex-1 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg">
              Create Entry
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Scheduled Shifts Modal -->
    <div v-if="showScheduledShiftsModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 w-full max-w-lg">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-bold text-white">Scheduled Shifts</h3>
          <button @click="showScheduledShiftsModal = false" class="text-slate-400 hover:text-white">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <div class="space-y-3">
          <div v-for="shift in scheduledShifts" :key="shift.id" class="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
            <div>
              <p class="text-white font-medium">{{ shift.jobTitle }}</p>
              <p class="text-sm text-slate-400">{{ shift.clientName }}</p>
              <p class="text-sm text-slate-400">{{ formatDate(shift.date) }} • {{ shift.scheduledStart }} - {{ shift.scheduledEnd }}</p>
            </div>
            <button @click="createFromScheduledShift(shift)" class="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg">
              Create Entry
            </button>
          </div>
        </div>
        
        <div class="mt-6">
          <button @click="showScheduledShiftsModal = false" class="w-full px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg">
            Close
          </button>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import AppLayout from '../components/layout/AppLayout.vue'

// Reactive data
const isTimerRunning = ref(false)

// Modal states
const showCreateEntryModal = ref(false)
const showScheduledShiftsModal = ref(false)

// Form data
const timesheetEntryForm = ref({
  date: '',
  jobTitle: '',
  clientName: '',
  startTime: '',
  endTime: '',
  description: '',
  hours: 0
})

const scheduledShifts = ref([
  {
    id: 1,
    date: '2024-01-20',
    jobTitle: 'Pipeline Maintenance',
    clientName: 'ExxonMobil',
    scheduledStart: '08:00',
    scheduledEnd: '17:00'
  },
  {
    id: 2,
    date: '2024-01-21',
    jobTitle: 'Safety Inspection',
    clientName: 'Chevron',
    scheduledStart: '07:30',
    scheduledEnd: '16:30'
  },
  {
    id: 3,
    date: '2024-01-22',
    jobTitle: 'Equipment Repair',
    clientName: 'BP',
    scheduledStart: '08:00',
    scheduledEnd: '17:00'
  }
])

const timeStats = ref({
  thisWeek: 42.5,
  thisWeekTarget: 40,
  pendingApproval: 3,
  thisMonth: 168.5,
  activeJobs: 4
})

const currentWeekEntries = ref([
  {
    id: 1,
    date: '2024-01-15',
    jobTitle: 'Pipeline Maintenance',
    clientName: 'ExxonMobil',
    startTime: '08:00',
    endTime: '17:00',
    hours: 9,
    status: 'Completed'
  },
  {
    id: 2,
    date: '2024-01-16',
    jobTitle: 'Safety Inspection',
    clientName: 'Chevron',
    startTime: '07:30',
    endTime: '16:30',
    hours: 9,
    status: 'Completed'
  },
  {
    id: 3,
    date: '2024-01-17',
    jobTitle: 'Equipment Repair',
    clientName: 'BP',
    startTime: '08:00',
    endTime: null,
    hours: 0,
    status: 'In Progress'
  },
  {
    id: 4,
    date: '2024-01-18',
    jobTitle: 'Emergency Response',
    clientName: 'Shell',
    startTime: '06:00',
    endTime: '18:00',
    hours: 12,
    status: 'Completed'
  },
  {
    id: 5,
    date: '2024-01-19',
    jobTitle: 'Pipeline Maintenance',
    clientName: 'ExxonMobil',
    startTime: '08:00',
    endTime: '17:00',
    hours: 9,
    status: 'Completed'
  }
])

const recentTimesheets = ref([
  {
    id: 1,
    period: 'Week of Jan 8-14',
    totalHours: 45.5,
    status: 'Approved',
    submittedDate: '2024-01-15'
  },
  {
    id: 2,
    period: 'Week of Jan 1-7',
    totalHours: 42.0,
    status: 'Approved',
    submittedDate: '2024-01-08'
  },
  {
    id: 3,
    period: 'Week of Dec 25-31',
    totalHours: 38.5,
    status: 'Pending',
    submittedDate: '2024-01-01'
  }
])

// Methods
const createTimesheetEntry = () => {
  console.log('Creating timesheet entry:', timesheetEntryForm.value)
  // Here you would typically make an API call
  alert('Timesheet entry created successfully!')
  showCreateEntryModal.value = false
  // Reset form
  timesheetEntryForm.value = {
    date: '',
    jobTitle: '',
    clientName: '',
    startTime: '',
    endTime: '',
    description: '',
    hours: 0
  }
}

const createFromScheduledShift = (shift: any) => {
  timesheetEntryForm.value = {
    date: shift.date,
    jobTitle: shift.jobTitle,
    clientName: shift.clientName,
    startTime: shift.scheduledStart,
    endTime: shift.scheduledEnd,
    description: '',
    hours: 0
  }
  showScheduledShiftsModal.value = false
  showCreateEntryModal.value = true
}

const submitTimesheet = () => {
  console.log('Submitting timesheet for approval')
  // Here you would typically make an API call
  alert('Timesheet submitted for approval!')
}

const deleteEntry = (entry: any) => {
  if (!window.confirm(`Delete the timesheet entry for ${entry.jobTitle}? This cannot be undone.`)) return
  currentWeekEntries.value = currentWeekEntries.value.filter((item) => item.id !== entry.id)
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  })
}

const formatDay = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'short'
  })
}

const getStatusClass = (status: string) => {
  const classes: { [key: string]: string } = {
    'Completed': 'bg-emerald-500/90',
    'In Progress': 'bg-blue-500/90',
    'Pending': 'bg-yellow-500/90',
    'Rejected': 'bg-red-500/90'
  }
  return classes[status] || 'bg-slate-500/90'
}
</script>

<style scoped>
.timesheet-stat-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 hover:border-emerald-500/50 transition-all duration-300;
}
</style>